import React from 'react';
import { ANALYTICS_NAVIGATION_MAP, DashboardTiles } from '@common/constants';
import { DoughnutChartTile } from '@pages/InsightsAndAnalytics/tiles/DoughnutChartTile';
import { NumberTile } from '@pages/InsightsAndAnalytics/tiles/NumberTile';

import styles from './styles.module.css';
import { useDashboardTileSaveMutation } from '@hooks/dashboard';
import { DashboardIcon } from '@cloudclevr/components';
import { MenuItem } from '@cloudclevr/components';
import {
  useNoLicencesSnapshotQuery,
} from '@hooks/security';
import { MicrosoftSecureScore } from '@pages/Dashboard/tiles/MicrosoftSecureScore';
import { ExternalTeamsAccess } from '@pages/Dashboard/tiles/ExternalTeamsAccess';
import { FailedLoginCountries } from '@pages/Dashboard/tiles/FailedLoginCountries';
import {
  FailedLoginAccounts,
  MicrosoftMFAStatus,
  MailboxesForwardingRules,
  OutDatedOperatingSystemsActivity,
  SharedMailboxesWithSignInEnabled,
  SuspiciousActivityTrend,
} from '@pages/Dashboard/tiles';
import { ExternalAppsMSData } from '@pages/Dashboard/tiles/ExternalAppsMSData';
import { FailedLoginTypes } from '@pages/Dashboard/tiles/FailedLoginTypes';
import { OutdatedBrowserActivity } from '@pages/Dashboard/tiles/OutdatedBrowserActivity';
import { DelegatedMailboxData } from '@pages/Dashboard/tiles/DelegatedMailboxData';
import useServicesStore from '@store/servicesStore';
import { isHelixDataVisible } from '@utils/index';
import { useSecuritySummaryQuery, useSummaryApisEnabled, useFreshnessIndicatorEnabled, pickSummary } from '@hooks/summary';
import { getLatestUpdateTime, formatFreshnessTimestamp } from '@hooks/summary/freshness';
import { toNoLicencesSnapshot, toExternalTeamsAccessSnapshot, toExternalApplicationsSnapshot } from '@hooks/summary/adapters';

const SecurityPage: React.FC = () => {
  const { helixStatus } = useServicesStore();
  const isHelixVisible = isHelixDataVisible(helixStatus, 'securityTab');

  // Summary API: single request for Persons + SharedMailboxes + SecureScores.
  // When summaryEnabled, legacy hooks are disabled from first render (no race condition).
  const summaryEnabled = useSummaryApisEnabled();
  const { data: summaryData, isLoading: isSummaryLoading } = useSecuritySummaryQuery();
  const personsSummary = pickSummary(summaryData, 'Persons');
  const sharedMailboxesSummary = pickSummary(summaryData, 'SharedMailboxes');
  const secureScoresSummary = pickSummary(summaryData, 'SecureScores');
  const userAssociatedTeamsSummary = pickSummary(summaryData, 'UserAssociatedTeams');
  const mailboxDelegationSummary = pickSummary(summaryData, 'MailboxDelegationAccesses');
  const externalAppsSummary = pickSummary(summaryData, 'ExternalApplications');
  // Legacy hooks disabled when feature flag is on.
  const { data: legacySignInData, isLoading: isLegacySignInLoading } =
    useNoLicencesSnapshotQuery({ enabled: !summaryEnabled });

  const signInEnabledData = summaryEnabled ? toNoLicencesSnapshot(personsSummary) : legacySignInData;
  const isSignInEnabledLoading = summaryEnabled ? isSummaryLoading : isLegacySignInLoading;

  const dashboardSaveMutation = useDashboardTileSaveMutation();

  const handleItemAction = (type: DashboardTiles) => () => {
    const getParametersData = () => {
      switch (type) {
        default:
          return {};
      }
    };

    const parametersData = getParametersData();

    dashboardSaveMutation.mutate({
      parameters: JSON.stringify(parametersData),
      tileType: type,
    });
  };

  const createMenuItems = (tileType: DashboardTiles): MenuItem[] => [
    {
      id: '0',
      label: 'Add to Dashboard',
      icon: <DashboardIcon classNames="mr-1" />,
      value: 'addToDashboard',
      action: handleItemAction(tileType),
    },
  ];

  const showFreshness = useFreshnessIndicatorEnabled();
  const freshnessTime = formatFreshnessTimestamp(getLatestUpdateTime(summaryData));

  return (
    <>
      {showFreshness && freshnessTime && (
        <p className="mb-2 text-right text-xs text-gray-400">Data as of {freshnessTime}</p>
      )}
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          {isHelixVisible && (
            <SuspiciousActivityTrend
              classNames="col-span-6"
              params={JSON.stringify({ days: '7' })}
            />
          )}

          <MicrosoftSecureScore params="" />

          {isHelixVisible && (summaryEnabled ? (
            <NumberTile
              classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
              headerTitle={'External apps'}
              isLoading={isSummaryLoading}
              data={(() => {
                const d = toExternalApplicationsSnapshot(externalAppsSummary);
                return d ? Object.values(d) : [];
              })()}
              percentageLabel=""
              buttonLabel={'External apps data'}
              linkPath={ANALYTICS_NAVIGATION_MAP['remoteAccessData']}
            />
          ) : (<ExternalAppsMSData params={''} />))}

          {summaryEnabled ? (
            <NumberTile
              classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
              contextData={createMenuItems(DashboardTiles.ExternalTeamsAccess)}
              headerTitle={'External Teams access'}
              isLoading={isSummaryLoading}
              data={(() => {
                const et = toExternalTeamsAccessSnapshot(userAssociatedTeamsSummary);
                return et ? Object.values(et) : [];
              })()}
              buttonLabel="External Teams access"
              linkPath={ANALYTICS_NAVIGATION_MAP['externalTeamsAccess']}
              bottomLabel="Users who aren't part of your business have access to Teams"
              percentageLabel=""
            />
          ) : (
            <ExternalTeamsAccess params="" />
          )}
        </div>
      </div>

      <h1 className={styles.header}>Login security</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          {isHelixVisible && (<>
            <FailedLoginAccounts
              params={JSON.stringify({ days: '30', mfaStatus: 'all' })}
            />
            <FailedLoginCountries params={JSON.stringify({ days: '30' })} />
            <FailedLoginTypes params={JSON.stringify({ days: '30' })} />
          </>)}
          <MicrosoftMFAStatus params={JSON.stringify({ userType: 'All' })} />
          <NumberTile
            contextData={createMenuItems(
              DashboardTiles.UsersWithSignInEnabledButNoLicenses
            )}
            classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
            percentageLabel=""
            headerTitle={'Users with sign-in enabled but no licenses'}
            isLoading={isSignInEnabledLoading}
            data={signInEnabledData ? Object.values(signInEnabledData) : []}
            buttonLabel={'Unassigned user data'}
            linkPath={ANALYTICS_NAVIGATION_MAP['signInEnabled']}
          />
        </div>
      </div>

      <h1 className={styles.header}>Mailboxes</h1>
      <div className={styles.pageContainer}>
        <div className={styles.pageGrid}>
          {isHelixVisible && (
            <MailboxesForwardingRules
              classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
              params={''}
              isSnapshot={false}
            />
          )}
          <SharedMailboxesWithSignInEnabled params="" />
          <DelegatedMailboxData params={JSON.stringify({ days: '30' })} />
        </div>
        {isHelixVisible && <>
          <h1 className={styles.header}>Outdated browsers and operating systems</h1>
          <div className={styles.pageContainer}>
            <div className={styles.pageGrid}>
              <OutdatedBrowserActivity
                classNames={'col-span-6 md:col-span-3 xl:col-span-2'}
                params={JSON.stringify({
                  days: '30',
                  browserType: '',
                  accountType: '',
                })}
                isSnapshot={false}
              />
              <OutDatedOperatingSystemsActivity
                params={JSON.stringify({
                  days: '30',
                  OSType: '',
                  accountType: '',
                })}
                isSnapshot={false}
              />
            </div>
          </div>
        </>}
      </div>
    </>
  );
};

export default SecurityPage;
